import React from 'react';
import { motion } from 'motion/react';
import { Type, Sparkles, MessageSquare, Target } from 'lucide-react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

const AICaptionGenerator = () => {
  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title="AI Caption Generator for Social Media - Write Better Copy"
        description="Write engaging captions for Instagram, LinkedIn, and TikTok with PlataPost's AI caption generator. High-converting copy in your unique brand voice."
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-20 mb-40">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full glass-card border border-white/5 text-indigo-400 text-[10px] font-black uppercase tracking-widest leading-none">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Conversion Focused</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">AI Caption <br /> <span className="text-indigo-500">Generator.</span></h1>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed font-medium">
              Never stare at a blank caption field again. Generate engaging, strategy-backed copy for all your social platforms in seconds.
            </p>
            <Link to="/dashboard" className="inline-flex px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
              Write My Next Caption
            </Link>
          </div>
          <div className="flex-1 w-full glass-card rounded-[4rem] p-12 border border-white/5 relative overflow-hidden group shadow-2xl shadow-indigo-600/5">
             <div className="absolute inset-0 bg-indigo-600/5 blur-[100px] pointer-events-none" />
             <div className="space-y-8 relative z-10">
               <div className="p-8 bg-white/5 rounded-3xl border border-white/10 border-l-4 border-l-indigo-500 group-hover:scale-105 transition-transform duration-500">
                  <p className="italic text-gray-300 mb-6 text-lg leading-relaxed">"Are you tired of spending hours on social media? Our AI manager just saved us 40 hours this month..."</p>
                  <div className="flex gap-3">
                    <span className="text-[10px] font-black text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-3 py-1.5 rounded-full uppercase tracking-widest">High Engagement</span>
                    <span className="text-[10px] font-black text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-full uppercase tracking-widest">LinkedIn Ready</span>
                  </div>
               </div>
               <div className="p-8 bg-white/[0.02] rounded-3xl border border-white/5 opacity-50">
                  <p className="text-gray-500 font-medium">Alternative variation focusing on business ROI...</p>
               </div>
             </div>
          </div>
        </div>

        <section className="mb-40">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight">Optimized for Conversion</h2>
            <p className="text-gray-400 font-medium max-w-xl mx-auto">Built on millions of high-performing data points to ensure your content connects.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: MessageSquare, title: "Hooks that Stick", desc: "Start every post with a thumb-stopping first line." },
              { icon: Target, title: "Calls to Action", desc: "Driving people to your site or bio with effective CTAs." },
              { icon: Target, title: "Brand Consistency", desc: "Maintains your unique vocabulary and tone." },
              { icon: Sparkles, title: "Emoji Strategy", desc: "Perfectly placed emojis to increase readability." }
            ].map((feature, i) => (
              <div key={i} className="text-center p-10 glass-card rounded-[2.5rem] border border-white/5 hover:bg-white/5 transition-all group">
                <div className="w-20 h-20 bg-white/5 border border-white/10 rounded-3xl flex items-center justify-center mx-auto mb-8 text-indigo-400 group-hover:scale-110 transition-transform shadow-xl shadow-indigo-600/5">
                  <feature.icon className="w-10 h-10" />
                </div>
                <h3 className="font-black text-white mb-3 text-xl tracking-tight">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed font-medium">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="text-center glass-card rounded-[4rem] p-24 border border-white/5 relative overflow-hidden shadow-2xl shadow-indigo-600/5">
           <div className="absolute inset-0 bg-indigo-600/5 blur-[120px] pointer-events-none" />
           <h2 className="text-4xl lg:text-6xl font-black text-white mb-10 tracking-tighter leading-tight relative z-10">Ready to write better copy?</h2>
           <Link to="/dashboard" className="relative z-10 inline-flex px-12 py-6 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
             Try it Free
           </Link>
        </div>
      </div>
    </div>
  );
};

export default AICaptionGenerator;
